import React, { createContext, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { CancelToken } from 'axios';
import useRestApiClient from 'src/hooks/useRestApiClient';

// import useSessionContext from 'src/hooks/useSessionContext';

const initialState = {
  instances: null
};

const InstancesContext = createContext({
  ...initialState,
  addInstance: () => Promise.resolve(),
  updateInstance: () => Promise.resolve(),
  refreshInstances: () => Promise.resolve()
});

export const InstancesProvider = (props) => {
  const { children } = props;
  const [instances, setInstances] = useState(initialState.instances);
  const [isFetching, setFetching] = useState(false);
  const [syncing, setSyncing] = useState([]);
  const request = useRef({});
  // const { session } = useSessionContext();
  const client = useRestApiClient();

  const addInstance = async (payload) => {
    const response = await client.post('/users/instances', payload);
    const instance = response.data.message;
    setInstances((prev) => [...prev, instance]);
    return instance;
  };

  const updateInstance = async (instanceId, payload) => {
    const response = await client.patch(
      `/users/instances/${instanceId}`,
      payload
    );
    setInstances((prev) => {
      const index = prev.findIndex(({ id }) => id === instanceId);
      prev[index] = {
        ...prev[index],
        ...payload
      };
      return prev;
    });
    return response.data.message;
  };

  const deleteInstance = async (instanceId) => {
    await client.delete(`/users/instances/${instanceId}`);
    setInstances((prev) => {
      return prev.filter((row) => row.id !== instanceId);
    });
  };

  const validatePlan = async (instanceId) => {
    let success = true;
    setSyncing((prev) => [...prev, instanceId]);
    try {
      await updateInstance(instanceId, { validate_plan: 1 });
    } catch (error) {
      success = false;
    }
    setSyncing((prev) => prev.filter((id) => id !== instanceId));
    return success;
  };

  const validateCredentials = (payload) =>
    client.post('/validate/account', {
      credentials: payload
    });

  const clearRequest = (key) => {
    request.current[key] = null;
  };

  const cancelRequest = (key) => {
    if (request.current[key]) {
      request.current[key].cancel('Operation cancelled by the user.');
      clearRequest(key);
    }
  };

  const registerRequest = (key, cancel) => {
    request.current[key] = { cancel };
  };

  const refreshInstances = async () => {
    cancelRequest('refreshInstances');

    try {
      setFetching(true);
      const result = await client.get('users/instances', {
        cancelToken: new CancelToken((cancel) => {
          registerRequest('refreshInstances', cancel);
        })
      });
      setInstances(result.data.message);
    } catch (error) {
      setInstances([]);
    }
    clearRequest('refreshInstances');
    setFetching(false);
  };

  const fetchInstance = async (instanceId) => {
    try {
      const result = await client.get(`users/instances/${instanceId}`);
      return result.data.message;
    } catch (error) {
      return null;
    }
  };

  return (
    <InstancesContext.Provider
      value={{
        isFetching,
        syncing,
        instances,
        addInstance,
        updateInstance,
        deleteInstance,
        validateCredentials,
        refreshInstances,
        fetchInstance,
        validatePlan
      }}
      {...props}
    >
      {children}
    </InstancesContext.Provider>
  );
};

InstancesProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default InstancesContext;
